import style from "./style.module.scss";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Icon, jnjChevronLeft } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import React, { createContext, Fragment, ReactNode } from "react";
import { mdiSlashForward } from "@mdi/js";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { mdiChevronLeft } from "@mdi/js";

import classnames from "classnames";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
interface Props {
    children: ReactNode;
    path?: Path[];
}

interface Path {
    name: string;
    active?: boolean;
    icon?: string;
    onClick?: () => void;
    showDivider?: boolean;
    disabled?: boolean;
}

export const BreadcrumbContext = createContext(null);

const Path = ({
    name,
    active = false,
    icon = null,
    onClick = () => {},
    showDivider = true,
    disabled = false,
}: Path): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    return (
        <BreadcrumbConsumer>
            {() => (
                <Fragment>
                    {active && (
                        <Fragment>
                            <span
                                onClick={() => {
                                    onClick();
                                }}
                                className={classnames(style.item, style.active)}
                                data-test-id={`Breadcrumb.${name}`}
                                onKeyDown={(e: KeyboardEvent): void => keyHandler(e.key, () => onClick())}
                                role="presentation"
                            >
                                {jnjFullBranded ? (
                                    <Typography className={style.divider} variant={"label-02"} weight={"500"}>
                                        {"/"}
                                    </Typography>
                                ) : (
                                    <Icon
                                        className={classnames(style.divider)}
                                        color="inherit"
                                        icon={mdiSlashForward}
                                        verticalAlign="middle"
                                    />
                                )}
                                {jnjFullBranded ? (
                                    <Icon
                                        className={style.chevron}
                                        color={"text-light"}
                                        icon={jnjChevronLeft}
                                        verticalAlign="middle"
                                        size="xs"
                                    />
                                ) : (
                                    <Icon
                                        className={classnames(style.chevron)}
                                        icon={mdiChevronLeft}
                                        verticalAlign="middle"
                                        size="medium"
                                    />
                                )}

                                <Typography size="s" weight={jnjFullBranded ? "500" : "600"} color="inherit">
                                    {name}
                                </Typography>
                            </span>
                        </Fragment>
                    )}
                    {!active && (
                        <div className={style.itemContainer}>
                            {showDivider &&
                                (jnjFullBranded ? (
                                    <Typography className={style.divider} variant={"label-02"} weight={"500"}>
                                        {"/"}
                                    </Typography>
                                ) : (
                                    <Icon
                                        className={classnames(style.divider)}
                                        color="inherit"
                                        icon={mdiSlashForward}
                                        verticalAlign="middle"
                                    />
                                ))}
                            <span
                                className={classnames(style.item, disabled ? style.disabled : null)}
                                onClick={() => {
                                    !disabled && onClick();
                                }}
                                data-test-id={`Breadcrumb.${name}`}
                                onKeyDown={(e: KeyboardEvent): void =>
                                    keyHandler(e.key, () => {
                                        !disabled && onClick();
                                    })
                                }
                                role="presentation"
                            >
                                {icon ? (
                                    <Icon color="inherit" size="small" icon={icon} verticalAlign="middle" />
                                ) : (
                                    <Typography size="s" weight={jnjFullBranded ? "500" : "600"} color="inherit">
                                        {name}
                                    </Typography>
                                )}
                            </span>
                        </div>
                    )}
                </Fragment>
            )}
        </BreadcrumbConsumer>
    );
};

export const Breadcrumb = ({ children }: Props) => {
    return (
        <BreadcrumbContext.Provider value={{ component: "breadcrumb" }}>
            <div className={style.element} id="breadcrumb" data-test-id="Breadcrumb">
                {children}
            </div>
        </BreadcrumbContext.Provider>
    );
};

Breadcrumb.Path = Path;

export const BreadcrumbConsumer = ({ children }: { children: any }) => (
    <BreadcrumbContext.Consumer>
        {(context: any) => {
            if (!context) {
                throw new Error("Breadcrumb compound components cannot be rendered outside the Breadcrumb component");
            }
            return children(context);
        }}
    </BreadcrumbContext.Consumer>
);
